import type { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core';

export type StructuredMarkerType = 'recommendation' | 'alignment' | 'tailor' | 'tips';
export type ParsedPartType = StructuredMarkerType | 'confidence' | 'markdown';

export interface ParsedPart {
  type: ParsedPartType;
  content: string;
}

export interface StructuredMarkerConfig {
  labelKey: string;
  icon: [IconPrefix, IconName];
}

export const smartAssistStructuredMarkers: Record<StructuredMarkerType, StructuredMarkerConfig> = {
  recommendation: { labelKey: 'smartAssist.structured.recommendation', icon: ['fas', 'star'] },
  alignment: { labelKey: 'smartAssist.structured.alignment', icon: ['fas', 'exclamation-triangle'] },
  tailor: { labelKey: 'smartAssist.structured.tailor', icon: ['fas', 'question-circle'] },
  tips: { labelKey: 'smartAssist.structured.tips', icon: ['fas', 'bolt'] },
};

interface MarkerMatch {
  type: ParsedPartType;
  inline: string;
}

interface CurrentBlock {
  type: ParsedPartType;
  lines: string[];
}

const markerLabels: { label: string; type: StructuredMarkerType }[] = [
  { label: 'Efficiency Tips for Advisors', type: 'tips' },
  { label: 'Ideas to Tailor Further', type: 'tailor' },
  { label: 'To Tailor Further', type: 'tailor' },
  { label: 'Alignment check', type: 'alignment' },
  { label: 'Recommendations', type: 'recommendation' },
  { label: 'Recommendation', type: 'recommendation' },
];

const isWhitespace = (value: string): boolean => {
  return value.length > 0 && value.trim() === '';
};
const isDigit = (value: string): boolean => {
  return value >= '0' && value <= '9';
};

const trimMarkdownPrefix = (line: string): string => {
  let value = line.trimStart();

  if ((value.startsWith('-') || value.startsWith('*')) && isWhitespace(value.charAt(1))) {
    value = value.slice(1).trimStart();
  } else {
    let digitCount = 0;
    while (digitCount < value.length && isDigit(value.charAt(digitCount))) digitCount += 1;
    if (
      digitCount > 0 &&
      (value.charAt(digitCount) === '.' || value.charAt(digitCount) === ')') &&
      isWhitespace(value.charAt(digitCount + 1))
    ) {
      value = value.slice(digitCount + 1).trimStart();
    }
  }

  let headingCount = 0;
  while (headingCount < value.length && value.charAt(headingCount) === '#') headingCount += 1;
  if (headingCount > 0 && headingCount <= 6 && isWhitespace(value.charAt(headingCount))) {
    value = value.slice(headingCount).trimStart();
  }

  return value;
};

const trimEmphasis = (value: string): string => {
  let next = value.trim();
  while (next.startsWith('**') || next.startsWith('__')) next = next.slice(2).trimStart();
  while (next.startsWith('*') || next.startsWith('_')) next = next.slice(1).trimStart();
  while (next.endsWith('**') || next.endsWith('__')) next = next.slice(0, -2).trimEnd();
  while (next.endsWith('*') || next.endsWith('_')) next = next.slice(0, -1).trimEnd();
  return next;
};

const trimMarkerSeparator = (value: string): string | null => {
  const next = trimEmphasis(value);
  if (!next) return '';
  if (next.startsWith(':')) return next.slice(1).trimStart();
  if (next.startsWith('-') || next.startsWith('–') || next.startsWith('—')) return next.slice(1).trimStart();
  return null;
};

const trimLeadingBreak = (value: string): string => {
  const trimmed = value.trimStart();
  const lower = trimmed.toLowerCase();
  if (!lower.startsWith('<br')) return trimmed.trim();

  const closeIndex = trimmed.indexOf('>');
  return closeIndex >= 0 ? trimmed.slice(closeIndex + 1).trim() : trimmed;
};

export const parseStructuredMarker = (line: string): MarkerMatch | null => {
  const candidate = trimEmphasis(trimMarkdownPrefix(line));
  const lowerCandidate = candidate.toLowerCase();

  for (const marker of markerLabels) {
    if (!lowerCandidate.startsWith(marker.label.toLowerCase())) continue;

    const inline = trimMarkerSeparator(candidate.slice(marker.label.length));
    if (inline === null) continue;
    return { type: marker.type, inline: trimLeadingBreak(inline) };
  }

  const confidenceLabel = 'confidence threshold';
  if (lowerCandidate.startsWith(confidenceLabel)) {
    let inline = candidate.slice(confidenceLabel.length).trimStart();
    if (inline.startsWith('(')) {
      const closeIndex = inline.indexOf(')');
      inline = closeIndex >= 0 ? inline.slice(closeIndex + 1).trimStart() : inline;
    }
    if (inline.startsWith(':')) inline = inline.slice(1).trimStart();
    return { type: 'confidence', inline };
  }

  return null;
};

const isStructuredClosingLine = (line: string): boolean => {
  return ['if you want', 'if you would like', 'if you need'].some((prefix) => {
    return line.trimStart().toLowerCase().startsWith(prefix);
  });
};

const isTailorClosingBreak = (line: string, current: CurrentBlock): boolean => {
  return current.type === 'tailor' && line.trim() === '' && current.lines.join('\n').trim().endsWith('.');
};

const splitLines = (value: string): string[] => {
  return value.replaceAll('\r\n', '\n').replaceAll('\r', '\n').split('\n');
};

export const parseSmartAssistResponse = (text: string): ParsedPart[] => {
  if (!text) return [];
  const parts: ParsedPart[] = [];
  let current: CurrentBlock = { type: 'markdown', lines: [] };

  const pushCurrent = (): void => {
    const content = current.lines.join('\n').trim();
    if (content) parts.push({ type: current.type, content });
  };

  for (const line of splitLines(text)) {
    const marker = parseStructuredMarker(line);
    if (!marker) {
      if (isTailorClosingBreak(line, current)) {
        pushCurrent();
        current = { type: 'markdown', lines: [] };
        continue;
      }
      if (current.type !== 'markdown' && isStructuredClosingLine(line)) {
        pushCurrent();
        current = { type: 'markdown', lines: [line] };
        continue;
      }
      current.lines.push(line);
      continue;
    }
    pushCurrent();
    current = { type: marker.type, lines: marker.inline ? [marker.inline] : [] };
  }

  pushCurrent();
  return parts;
};

const stripMarkdownLinks = (value: string): string => {
  let output = '';
  let index = 0;

  while (index < value.length) {
    const openLabel = value.indexOf('[', index);
    if (openLabel < 0) {
      output += value.slice(index);
      break;
    }

    const closeLabel = value.indexOf(']', openLabel + 1);
    const openHref = closeLabel >= 0 ? value.indexOf('(', closeLabel + 1) : -1;
    const closeHref = openHref >= 0 ? value.indexOf(')', openHref + 1) : -1;

    if (closeLabel < 0 || openHref !== closeLabel + 1 || closeHref < 0) {
      output += value.slice(index, openLabel + 1);
      index = openLabel + 1;
      continue;
    }

    output += value.slice(index, openLabel);
    output += value.slice(openLabel + 1, closeLabel);
    index = closeHref + 1;
  }

  return output;
};

const stripMarkdownDecorators = (value: string): string => {
  const markdownDecorators = new Set(['*', '_', '`', '~', '>', '#']);
  let output = '';
  for (const char of value) {
    if (!markdownDecorators.has(char)) output += char;
  }
  return output;
};

export const plainTextFromMarkdown = (value: string): string => {
  return stripMarkdownDecorators(stripMarkdownLinks(value)).trim();
};

const stripListPrefix = (line: string): string => {
  return trimMarkdownPrefix(line);
};

export const getTipItems = (content: string): string[] => {
  const lineItems = splitLines(content)
    .map((line) => {
      return plainTextFromMarkdown(stripListPrefix(line));
    })
    .filter(Boolean);
  if (lineItems.length > 1) return lineItems;
  return content
    .split(';')
    .flatMap((part) => {
      return part.split('|');
    })
    .map(plainTextFromMarkdown)
    .filter(Boolean);
};

export const getConfidencePercent = (content: string): number | null => {
  for (let index = 0; index < content.length; index += 1) {
    if (!isDigit(content.charAt(index))) continue;

    let end = index;
    while (end < content.length && isDigit(content.charAt(end))) end += 1;
    if (content.charAt(end) === '.') {
      end += 1;
      while (end < content.length && isDigit(content.charAt(end))) end += 1;
    }

    let cursor = end;
    while (cursor < content.length && isWhitespace(content.charAt(cursor))) cursor += 1;
    if (content.charAt(cursor) === '%') {
      return Math.max(0, Math.min(100, Number(content.slice(index, end))));
    }
    index = end;
  }

  return null;
};
